import { create } from 'zustand'
import type { Project, Folder, SidebarApp, CodexSession, GrokSession, GeminiSession, KimiSession, Tab, DiscoveredAgent, TabType, TerminalInstance, SplitViewState, SplitLayout, SplitPanel, PendingSelection, AppNotification, NotificationPrefs, NotificationSource, NotificationDestinationPrefs, OverlayState, SidebarState, LinkTarget, VoiceSettings, VoiceProfile, VoiceAppKey, ChatProvider, PmMessage, AgentCapabilities } from '../types'
import { DEFAULT_NOTIFICATION_PREFS, DEFAULT_VOICE_SETTINGS, RAW_VOICE_PROFILE } from '../types'
import type { ThemeChoice } from '../lib/theme'
import { undeferOpenedTabs } from '../lib/deferredSpawn'

// Dispatch resize at staggered intervals so terminals re-fit after React renders
function dispatchResize() {
  const fire = () => window.dispatchEvent(new Event('resize'))
  setTimeout(fire, 50)
  setTimeout(fire, 150)
  setTimeout(fire, 400)
}

export interface AppState {
  // Projects
  projects: Project[]
  activeProjectId: string | null
  sidebarState: SidebarState

  // Theme
  themeChoice: ThemeChoice
  setThemeChoice: (choice: ThemeChoice) => void

  // Tabs - per folder
  tabs: Tab[]
  activeTabId: string | null
  activeFolderId: string | null
  lastFolderPerProject: Record<string, string> // projectId -> last active folderId
  lastTabPerFolder: Record<string, string>     // folderId -> last active tabId
  /**
   * shareId -> the tab THIS user last had open in that share.
   *
   * The share-side twin of `lastTabPerFolder`. Landing rule, in order:
   *   1. this map — where you were last time
   *   2. the OWNER's active tab, pushed with `share-attached`
   *   3. the first tab in scope
   * Which is the same shape `setActiveProject` uses for a real project: your
   * own memory first, a sensible default second, index 0 only as a floor.
   */
  lastTabPerShare: Record<string, string>

  // Project Manager full-screen chat — when set, App.tsx renders
  // ProjectManagerView filling the main area (no tab, no TabBar) for this
  // project. Cleared by setActiveTab/setActiveFolder so any navigation away
  // from the chat (clicking a folder, session tab, etc.) leaves it.
  activePmProjectId: string | null
  setActivePmProject: (id: string | null) => void
  /**
   * Experimental toggle for the whole Project Manager feature (Settings →
   * Experimental). Off: no PM home view, no worker-session watcher, and
   * `pm:open` is never sent — so the main-process orchestrator (and its 500ms
   * transcript poll) never starts. On: everything comes straight back, no
   * restart needed. Defaults OFF — the toggle is a deliberate opt IN.
   */
  pmEnabled: boolean
  setPmEnabled: (on: boolean) => void
  loadPmEnabled: () => Promise<void>

  /**
   * Where a clicked link goes (Settings → Browser). 'in-app' opens a
   * CodeFlows browser tab — the only behaviour before this setting existed,
   * and so the default; 'system' hands the URL to the OS default browser.
   *
   * Lives in the store rather than being read from settings at click time
   * because the decision has to be synchronous: `window.open` after an await
   * is a popup as far as Safari is concerned, and the mobile client runs
   * there. Hydrated once at startup by App.tsx.
   */
  openLinksIn: LinkTarget
  setOpenLinksIn: (target: LinkTarget) => void
  loadOpenLinksIn: () => Promise<void>

  // Folders
  folders: Record<string, Folder[]>
  ptyMap: Record<string, string> // sessionId -> ptyId

  // Sidebar Apps
  sidebarApps: Record<string, SidebarApp[]>
  activeSidebarAppId: string | null
  appPickerProject: string | null

  // Codex Sessions
  codexSessions: Record<string, CodexSession[]>
  // Grok Sessions
  grokSessions: Record<string, GrokSession[]>
  // Gemini Sessions
  geminiSessions: Record<string, GeminiSession[]>
  // Kimi Sessions
  kimiSessions: Record<string, KimiSession[]>

  // Discovery
  discoveredAgents: Record<string, DiscoveredAgent[]>

  // Project Manager chat — projectId -> messages / mid-reply indicator.
  // Main process is the source of truth (pmGetState/pm:message); this is
  // just the renderer's live view of it.
  pmChats: Record<string, PmMessage[]>
  pmThinking: Record<string, boolean>

  // Project dialog
  showProjectDialog: boolean
  // Optional prefill for the project dialog. When set, ProjectDialog
  // seeds name + working directory from it and invokes `onCreated` after
  // the user confirms. Used by the global search palette to add an
  // unknown project and then auto-open the matched session.
  pendingProjectPrefill: {
    name: string
    workingDirectory: string
    onCreated?: (projectId: string) => void
  } | null
  setPendingProjectPrefill: (
    value: { name: string; workingDirectory: string; onCreated?: (projectId: string) => void } | null
  ) => void

  // Global Claude search palette
  globalSearchOpen: boolean
  setGlobalSearchOpen: (open: boolean) => void
  globalSearchPendingMatch: Record<string, { query: string; fallbackPreview?: string; projectPath?: string; jsonlSessionId?: string } | null>
  setGlobalSearchPendingMatch: (sessionId: string, value: { query: string; fallbackPreview?: string; projectPath?: string; jsonlSessionId?: string } | null) => void

  // Actions - Projects
  setProjects: (projects: Project[]) => void
  addProject: (project: Project) => void
  removeProject: (id: string) => void
  setActiveProject: (id: string | null) => void
  collapseSidebar: () => void
  hideSidebar: () => void
  expandSidebar: () => void
  showSidebar: () => void

  // Actions - Active Folder
  setActiveFolder: (id: string | null) => void

  // Actions - Tabs
  addTab: (tab: Tab) => void
  removeTab: (id: string) => void
  replaceTab: (oldId: string, newTab: Tab) => void
  setActiveTab: (id: string) => void
  /** Remember which tab of a share this user was on. */
  rememberShareTab: (shareId: string, tabId: string) => void
  updateTab: (id: string, updates: Partial<Tab>) => void

  // Actions - Folder Data
  setFolders: (projectId: string, folders: Folder[]) => void
  addFolder: (folder: Folder) => void
  updateFolder: (projectId: string, folderId: string, updates: Partial<Folder>) => void
  removeFolder: (projectId: string, folderId: string) => void
  setPtyMapping: (sessionId: string, ptyId: string) => void
  removePtyMapping: (sessionId: string) => void

  // Actions - Sidebar Apps
  setSidebarApps: (projectId: string, apps: SidebarApp[]) => void
  addSidebarApp: (app: SidebarApp) => void
  removeSidebarApp: (projectId: string, appId: string) => void
  setActiveSidebarApp: (id: string | null) => void
  setAppPickerProject: (projectId: string | null) => void

  // Actions - Codex Sessions
  setCodexSessions: (projectId: string, sessions: CodexSession[]) => void
  addCodexSession: (session: CodexSession) => void
  updateCodexSession: (projectId: string, sessionId: string, updates: Partial<CodexSession>) => void
  removeCodexSession: (projectId: string, sessionId: string) => void
  // Actions - Grok Sessions
  setGrokSessions: (projectId: string, sessions: GrokSession[]) => void
  addGrokSession: (session: GrokSession) => void
  updateGrokSession: (projectId: string, sessionId: string, updates: Partial<GrokSession>) => void
  removeGrokSession: (projectId: string, sessionId: string) => void
  // Actions - Gemini Sessions
  setGeminiSessions: (projectId: string, sessions: GeminiSession[]) => void
  addGeminiSession: (session: GeminiSession) => void
  updateGeminiSession: (projectId: string, sessionId: string, updates: Partial<GeminiSession>) => void
  removeGeminiSession: (projectId: string, sessionId: string) => void
  // Actions - Kimi Sessions
  setKimiSessions: (projectId: string, sessions: KimiSession[]) => void
  addKimiSession: (session: KimiSession) => void
  updateKimiSession: (projectId: string, sessionId: string, updates: Partial<KimiSession>) => void
  removeKimiSession: (projectId: string, sessionId: string) => void

  // Actions - Terminals
  terminals: Record<string, TerminalInstance[]>
  setTerminals: (projectId: string, terminals: TerminalInstance[]) => void
  addTerminal: (terminal: TerminalInstance) => void
  updateTerminal: (projectId: string, terminalId: string, updates: Partial<TerminalInstance>) => void
  removeTerminal: (projectId: string, terminalId: string) => void

  // Actions - Discovery
  setDiscoveredAgents: (projectId: string, agents: DiscoveredAgent[]) => void

  // Actions - Project Manager
  /** Replaces a project's whole chat — used to hydrate from pmGetState on mount. */
  setPmMessages: (projectId: string, msgs: PmMessage[]) => void
  /** Appends one message, deduping by id — pmGetState's snapshot and the live
   *  onPmMessage stream can both deliver the same message during mount. */
  appendPmChat: (projectId: string, msg: PmMessage) => void
  setPmThinking: (projectId: string, v: boolean) => void

  // Pending initial messages for new folders
  pendingMessages: Record<string, string>
  setPendingMessage: (folderId: string, message: string | null) => void

  /**
   * Pending "transferred from another model" banner for the *next* terminal
   * mount in this session — set by SwitchAgentControl right after it creates
   * the switched session's tab, consumed once (like pendingMessages) by
   * ClaudeTerminal/CodexTerminal/GrokTerminal on mount and written straight
   * into the xterm buffer (never sent through the pty). Grok's TUI never
   * visually replays `--resume`'d history even for its own genuine sessions,
   * so this is the only user-visible confirmation the transfer worked.
   */
  pendingTransferNotices: Record<string, { sourceModel: 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi'; messageCount: number; toolCount: number }>
  setPendingTransferNotice: (
    sessionId: string,
    notice: { sourceModel: 'claude' | 'codex' | 'grok' | 'gemini' | 'kimi'; messageCount: number; toolCount: number } | null
  ) => void

  /**
   * Pending MCP whitelist for the *next* Claude session spawn in this folder.
   * Consumed once by ClaudeTerminal on mount, then cleared. `null` (or absent)
   * means "use Claude's default MCP loading" (all MCPs enabled).
   */
  pendingMcpSelections: Record<string, string[]>
  setPendingMcpSelection: (folderId: string, enabled: string[] | null) => void

  /**
   * Pending permission mode for the *next* Claude session spawn in this folder.
   * Resolved at Start time (folder override → project default). Consumed once.
   */
  pendingPermissionModes: Record<string, 'default' | 'plan' | 'dontAsk' | 'acceptEdits' | 'auto' | 'bypassPermissions' | 'manual'>
  setPendingPermissionMode: (
    folderId: string,
    mode: 'default' | 'plan' | 'dontAsk' | 'acceptEdits' | 'auto' | 'bypassPermissions' | 'manual' | null
  ) => void

  /**
   * Pending reasoning effort for the *next* Claude session spawn in this folder.
   * Same consume-once pattern as the other pending-* maps.
   */
  pendingEffortLevels: Record<string, 'low' | 'medium' | 'high' | 'xhigh' | 'max'>
  setPendingEffortLevel: (
    folderId: string,
    effort: 'low' | 'medium' | 'high' | 'xhigh' | 'max' | null
  ) => void

  /**
   * Pending Claude model (`--model`) for the *next* session spawn in this folder.
   * Same consume-once pattern as the other pending-* maps.
   */
  pendingModels: Record<string, string>
  setPendingModel: (folderId: string, model: string | null) => void

  /**
   * Live per-CLI capabilities (models + reasoning efforts), probed at app boot
   * from the installed CLIs and cached. `null` until first load.
   */
  agentCapabilities: AgentCapabilities | null
  loadAgentCapabilities: (force?: boolean) => Promise<void>

  // Persisted draft messages for Discovery "+ Add Claude" tabs — keyed by folder ID.
  // Survives navigation and app restarts. Cleared on session start or tab/folder deletion.
  discoveryDrafts: Record<string, string>
  setDiscoveryDraft: (folderId: string, draft: string | null) => void

  // In-memory "unseen status" markers — folder has a new status the user hasn't acknowledged.
  // Value is the status that triggered it ('waiting' or 'active').
  // Cleared when the user clicks the folder. Not persisted (resets on app restart).
  unseenFolders: Record<string, 'waiting' | 'active'>
  markFolderUnseen: (folderId: string, status: 'waiting' | 'active') => void
  markFolderSeen: (folderId: string) => void

  // Pending selection context (array of attached files per folder)
  pendingSelections: Record<string, PendingSelection[] | null>
  setPendingSelection: (folderId: string, sel: PendingSelection | null) => void
  setPendingSelections: (folderId: string, sels: PendingSelection[] | null) => void
  addPendingSelection: (folderId: string, sel: PendingSelection) => void
  removePendingSelection: (folderId: string, idx: number) => void

  // Actions - Reorder
  reorderTabs: (fromId: string, toId: string) => void
  reorderTopLevel: (fromId: string, toId: string) => void
  /** Move a multi-selection of top-level tabs as a unit. Tabs inside
   *  a split view are skipped — the split view is the top-level item. */
  reorderTopLevelGroup: (fromIds: string[], toId: string) => void
  reorderProjects: (fromId: string, toId: string) => void
  reorderFolders: (projectId: string, fromId: string, toId: string) => void

  // Split View
  splitViews: SplitViewState[]
  activeSplitViewId: string | null
  splitViewsPerFolder: Record<string, { splitViews: SplitViewState[]; activeSplitViewId: string | null }>
  lastLayoutPerCount: Record<number, SplitLayout> // remembers preferred layout per panel count
  lastRatiosPerLayout: Record<string, { vr: number; hr: number }> // remembers resize ratios per layout type
  selectedTabIds: string[]
  selectionAnchorId: string | null
  toggleTabSelection: (tabId: string) => void
  selectTabRange: (tabIds: string[]) => void
  clearTabSelection: () => void
  addToSplitView: (tabIds: string[], targetSvId?: string) => void
  closeSplitView: (id?: string) => void
  setActivePanelId: (panelId: string) => void
  setPanelActiveTab: (panelId: string, tabId: string) => void
  updateSplitRatio: (type: 'horizontal' | 'vertical', ratio: number) => void
  changeSplitLayout: (layout: SplitLayout) => void
  moveTabToPanel: (tabId: string, targetPanelId: string) => void
  removeFromSplitPanel: (tabId: string) => void
  reorderSplitPanels: (svId: string, fromTabId: string, toTabId: string) => void
  renameSplitView: (name: string, id?: string) => void
  activateSplitView: (id?: string) => void

  // Actions - UI
  setShowProjectDialog: (show: boolean) => void

  // Favorited apps shown in the + dropdown — capped to MAX_FAVORITE_APPS.
  // Persisted via window.api.setSetting('favoriteApps', ...) in App.tsx.
  favoriteApps: string[]
  setFavoriteApps: (ids: string[]) => void
  toggleFavoriteApp: (id: string) => void

  // Cross-component request to highlight a row inside the AiTables webview.
  // The timestamp ensures even repeat requests for the same rowId trigger
  // a fresh effect run in AiTablesView (otherwise React would skip re-renders
  // if the value didn't change). `url` is the page the row was captured on
  // — the AiTables hook compares against the current webview URL and only
  // navigates if they differ. `tabId` scopes the request to ONE tab so
  // when multiple AiTables tabs are open only the chosen one highlights.
  aitablesHighlightRequest: { rowId: string; url?: string; tabId: string; ts: number } | null
  requestAitablesHighlight: (rowId: string, tabId: string, url?: string) => void

  // Map of AiTables row UUID → source URL the row was on when sent. Used by
  // the chip click handler in ClaudeTerminal.tsx to look up where to navigate.
  aitablesRowUrls: Record<string, string>
  setAitablesRowUrl: (rowId: string, url: string) => void

  // Same pattern for AiBoards: re-highlight a set of selected Konva
  // shapes inside the AiBoards webview when the user clicks the chip.
  // tabId scopes the request to one tab.
  aiboardsHighlightRequest: { elementIds: string[]; url?: string; tabId: string; ts: number } | null
  requestAiboardsHighlight: (elementIds: string[], tabId: string, url?: string) => void

  // Basecamp doesn't have a "highlight" — clicking the chip just navigates
  // the Basecamp webview to the URL the card was sent from. Same fresh-ts
  // pattern + tabId scoping so only the chosen Basecamp tab navigates.
  basecampNavigateRequest: { url: string; tabId: string; ts: number } | null
  requestBasecampNavigate: (url: string, tabId: string) => void

  // Map of AiBoards capture file paths → { elementIds, url } captured at
  // send time. Lives separately from the chip itself because element ID
  // lists are unbounded and we don't want to bloat the pending selection.
  aiboardsCaptureElements: Record<string, string[]>
  aiboardsCaptureUrls: Record<string, string>
  setAiboardsCaptureElements: (filePath: string, elementIds: string[]) => void
  setAiboardsCaptureUrl: (filePath: string, url: string) => void

  // In-app notifications surfaced in the TitleBar bell popover. Covers
  // both live unread-count mirrors (Basecamp) and transient event
  // notifications (Claude session state). Entries are keyed by a
  // stable id of the form `${source}:${kind}:${refId?}` — see
  // AppNotification in types.ts for the full contract. Nothing is
  // persisted; the scanner (Basecamp) / event emitter (Claude) re-
  // hydrates the list on every session boot.
  appNotifications: AppNotification[]
  upsertAppNotification: (n: AppNotification) => void
  removeAppNotification: (id: string) => void
  markAppNotificationRead: (id: string) => void
  markAllAppNotificationsRead: () => void
  clearAppNotifications: () => void

  // Per-source notification destination prefs — computer (native OS
  // toasts) / app (in-app bell). Hydrated from `notificationPrefs`
  // setting on App mount with a merge against DEFAULT_NOTIFICATION_PREFS
  // so stale settings blobs don't break when we add new sources.
  // Consulted by `fireNotification` in app/src/lib/notifications.ts —
  // the single dispatcher every notification flows through.
  notificationPrefs: NotificationPrefs
  setNotificationPrefs: (prefs: NotificationPrefs) => void
  setNotificationPref: (source: NotificationSource, destination: keyof NotificationDestinationPrefs, enabled: boolean) => void

  // Voice input — global default + per-app overrides. Switching the
  // profile inline from a surface (Claude / Codex / Git) writes
  // `perApp[appKey]`, never the global default. The Settings UI is the
  // only place that mutates `defaultProfileId`. The built-in `Raw`
  // profile (id 'raw') has an empty prompt and is non-deletable; the
  // VoiceButton short-circuits the transform IPC when it's the active
  // profile. Hydrated from `voiceSettings` setting on App mount; every
  // mutator persists via `window.api.setSetting('voiceSettings', ...)`.
  voiceSettings: VoiceSettings
  setVoiceSettings: (settings: VoiceSettings) => void
  voiceAddProfile: (profile: { name: string; prompt: string }) => string
  voiceUpdateProfile: (id: string, patch: { name?: string; prompt?: string }) => void
  voiceDeleteProfile: (id: string) => void
  voiceSetDefault: (id: string) => void
  voiceSetPerApp: (appKey: VoiceAppKey, profileId: string) => void
  voiceClearPerApp: (appKey: VoiceAppKey) => void
  voiceSetKeybind: (accel: string | null) => void

  // Chat panel — tabbed: each provider can be open at most once.
  chatOpen: boolean
  chatWidth: number
  chatProviders: ChatProvider[]
  activeChatProvider: ChatProvider | null
  toggleChat: () => void
  setChatWidth: (width: number) => void
  openChatProvider: (provider: ChatProvider) => void
  closeChatProvider: (provider: ChatProvider) => void
  setActiveChatProvider: (provider: ChatProvider) => void
  reorderChatProviders: (fromProvider: ChatProvider, toProvider: ChatProvider) => void

  // Overlay mode
  overlayState: OverlayState
  setOverlayState: (state: OverlayState) => void

  // Most recently ACTIVE Basecamp tab id — updated on every setActiveTab
  // whose target is a basecamp tab. The bell-popover click handler uses
  // this to decide which Basecamp tab to land on: "open the thing I
  // was last looking at". Resets to null when the last Basecamp tab is
  // closed.
  lastBasecampTabId: string | null

  // Claude-session restart coordination. Both are in-memory only —
  // meaningful only within the lifetime of the current renderer process.
  //
  //   - `restartingSessionIds` marks sessions whose PTY is about to die
  //     on purpose (we cycle them after a Claude CLI update, or when
  //     switching the session's account). ClaudeTerminal.tsx's pty-exit
  //     handler reads this and skips its usual "PTY died ⇒ remove tab"
  //     cleanup — otherwise the tab would disappear instead of reconnecting.
  //   - `sessionRestartTokens` bumps per session to force a remount of
  //     the mounted `ClaudeTerminalSession` component (keyed with the
  //     current token), which re-runs the spawn/resume flow — against the
  //     freshly-installed claude binary, or under the newly-selected account.
  restartingSessionIds: Set<string>
  sessionRestartTokens: Record<string, number>
  markSessionRestarting: (sessionId: string) => void
  unmarkSessionRestarting: (sessionId: string) => void
  bumpSessionRestartToken: (sessionId: string) => void
  /** Schedule a restart for every Claude session whose PTY is alive. */
  restartAllClaudeSessions: () => Promise<void>
  /** Restart every running Codex session (global account switch/remove). */
  restartAllCodexSessions: () => Promise<void>
  /** Restart every running Grok session (global account switch/remove). */
  restartAllGrokSessions: () => Promise<void>
  /** Restart every running Gemini session (global account switch/remove). */
  restartAllGeminiSessions: () => Promise<void>
  /** Restart every running Kimi session (global account switch/remove). */
  restartAllKimiSessions: () => Promise<void>
  /** Restart a single Claude session (kill PTY + remount to re-spawn). */
  restartClaudeSession: (sessionId: string) => Promise<void>
  /** Point every Claude folder at `accountId` and restart the running ones. */
  switchAllSessionsToAccount: (accountId: string) => Promise<void>
  /** After deleting an account, move its folders back to default + restart them. */
  reassignSessionsToDefault: (removedAccountId: string) => Promise<void>

  // Tabs restored from disk at boot that haven't been opened yet in this run,
  // and so have deliberately NOT been mounted — see lib/deferredSpawn.ts for
  // why (mounting a session tab spawns its CLI, and every CLI spawns its own
  // copy of every configured MCP server). In-memory only.
  //
  // Lives in the store rather than in App.tsx because un-deferring is not
  // purely a "user clicked the tab" event: the Project Manager reopens a
  // session in the BACKGROUND, without ever touching activeTabId, and must be
  // able to force a deferred tab live.
  deferredTabIds: Set<string>
  /** True once a boot restore has seeded `deferredTabIds`. */
  deferredSeeded: boolean
  /** Seed the deferred set from a boot restore. First call per run wins, so a
   *  StrictMode double-invoked init can't re-defer already-opened tabs. */
  seedDeferredTabIds: (tabIds: string[]) => void
  /** Mount (and therefore spawn) the given tabs now. No-op for unknown ids. */
  undeferTabs: (tabIds: string[]) => void
}

// Pick the best layout for a panel count, using user's last preference
const LAYOUT_MAX_PANELS: Record<string, number> = { '2-col': 2, '2-row': 2, '3-col': 3, '2-col-1-bottom': 3, '1-top-2-col-bottom': 3, '2x2': 4 }

function pickLayout(count: number, prefs: Record<number, SplitLayout>, current?: SplitLayout): SplitLayout {
  // If user has a saved preference for this count, use it (only if it fits)
  const saved = prefs[count]
  if (saved && (LAYOUT_MAX_PANELS[saved] || 99) >= count) return saved
  // Otherwise fall back to defaults
  if (count <= 2) return current === '2-row' ? '2-row' : '2-col'
  if (count === 3) return '2-col-1-bottom'
  return '2x2'
}

// Downgrade layout when panels are removed
function fitLayout(current: SplitLayout, count: number, prefs?: Record<number, SplitLayout>): SplitLayout {
  if (prefs?.[count]) return prefs[count]
  if (count <= 2) return current === '2-row' ? '2-row' : '2-col'
  if (count === 3) {
    if (current === '2x2') return '2-col-1-bottom'
    if (current === '3-col' || current === '2-col-1-bottom' || current === '1-top-2-col-bottom') return current
    return '2-col-1-bottom'
  }
  return current
}

// Helper: get active split view from state
function getASV(state: { splitViews: SplitViewState[]; activeSplitViewId: string | null }, id?: string) {
  const svId = id || state.activeSplitViewId
  return svId ? state.splitViews.find((sv) => sv.id === svId) || null : null
}
// Helper: update a split view in the array
function mapSV(views: SplitViewState[], id: string, fn: (sv: SplitViewState) => SplitViewState): SplitViewState[] {
  return views.map((sv) => sv.id === id ? fn(sv) : sv)
}

export const useAppStore = create<AppState>((set, get) => ({
  projects: [],
  activeProjectId: null,
  sidebarState: 'expanded',
  // Read initial theme from URL hash (set by main process from electron-store)
  // to prevent the useEffect from flashing 'system' → resolved before hydration.
  themeChoice: (() => {
    const m = typeof location !== 'undefined' && location.hash.match(/theme=(light|dark)/)
    return m ? m[1] as 'light' | 'dark' : 'system' as const
  })(),
  tabs: [],
  activeTabId: null,
  activeFolderId: null,
  lastFolderPerProject: {},
  lastTabPerFolder: {},
  lastTabPerShare: {},
  activePmProjectId: null,
  // Single choke point for entering the PM view (sidebar project click, and
  // anything else via pmTab's openProjectManager) — guarding here means a
  // disabled PM can't be opened from any call site.
  setActivePmProject: (id) => {
    if (id !== null && !get().pmEnabled) return
    set({ activePmProjectId: id })
  },
  pmEnabled: false,
  setPmEnabled: (on) => {
    // Clear the open PM view when switching off, or it would keep rendering.
    set(on ? { pmEnabled: true } : { pmEnabled: false, activePmProjectId: null })
    // Optional-chained: the in-memory flip must still apply if the preload
    // bridge is missing, so the UI never desyncs from what the app gates on.
    try { void window.api?.setSetting('pmEnabled', on) } catch { /* not persisted */ }
  },
  loadPmEnabled: async () => {
    try {
      const v = await window.api?.getSetting('pmEnabled')
      // Only an explicit `true` enables it: an absent key (fresh install, or
      // any read failure) must leave the feature OFF.
      if (v === true) set({ pmEnabled: true })
    } catch { /* keep the default */ }
  },
  openLinksIn: 'in-app',
  setOpenLinksIn: (target) => {
    set({ openLinksIn: target })
    // Optional-chained like setPmEnabled: the in-memory flip must still apply
    // when the preload bridge is missing, so clicks follow the choice even if
    // it fails to persist.
    try { void window.api?.setSetting('openLinksIn', target) } catch { /* not persisted */ }
  },
  loadOpenLinksIn: async () => {
    try {
      const v = await window.api?.getSetting('openLinksIn')
      // Anything unrecognised — absent, or a value from an older build — is
      // left as the in-app default rather than guessed at.
      if (v === 'system' || v === 'in-app') set({ openLinksIn: v })
    } catch { /* keep the default */ }
  },
  folders: {},
  sidebarApps: {},
  activeSidebarAppId: null,
  appPickerProject: null,
  codexSessions: {},
  grokSessions: {},
  geminiSessions: {},
  kimiSessions: {},
  terminals: {},
  ptyMap: {},
  pendingMessages: {},
  pendingTransferNotices: {},
  pendingMcpSelections: {},
  pendingPermissionModes: {},
  pendingEffortLevels: {},
  pendingModels: {},
  agentCapabilities: null,
  discoveryDrafts: {},
  discoveredAgents: {},
  pmChats: {},
  pmThinking: {},
  unseenFolders: {},
  pendingSelections: {},
  splitViews: [],
  activeSplitViewId: null,
  splitViewsPerFolder: {},
  lastLayoutPerCount: { 2: '2-col', 3: '2-col-1-bottom', 4: '2x2' },
  lastRatiosPerLayout: {},
  selectedTabIds: [],
  selectionAnchorId: null,
  showProjectDialog: false,
  pendingProjectPrefill: null,
  globalSearchOpen: false,
  globalSearchPendingMatch: {},
  favoriteApps: [],
  aitablesHighlightRequest: null,
  aitablesRowUrls: {},
  aiboardsHighlightRequest: null,
  aiboardsCaptureElements: {},
  aiboardsCaptureUrls: {},
  basecampNavigateRequest: null,
  appNotifications: [],
  notificationPrefs: DEFAULT_NOTIFICATION_PREFS,
  voiceSettings: DEFAULT_VOICE_SETTINGS,
  chatOpen: false,
  chatWidth: 350,
  chatProviders: ['chatflows'],
  activeChatProvider: 'chatflows',
  overlayState: 'off',
  lastBasecampTabId: null,
  restartingSessionIds: new Set<string>(),
  sessionRestartTokens: {},
  deferredTabIds: new Set<string>(),
  deferredSeeded: false,

  // Projects
  setProjects: (projects) => set({ projects }),
  addProject: (project) => set((s) => ({ projects: [...s.projects, project] })),
  removeProject: (id) =>
    set((s) => ({
      projects: s.projects.filter((p) => p.id !== id),
      tabs: s.tabs.filter((t) => t.projectId !== id),
      activeProjectId: s.activeProjectId === id ? null : s.activeProjectId,
      activeTabId:
        s.activeProjectId === id
          ? null
          : s.activeTabId,
      activePmProjectId: s.activePmProjectId === id ? null : s.activePmProjectId,
    })),
  rememberShareTab: (shareId, tabId) => {
    if (!shareId || !tabId) return
    const cur = get().lastTabPerShare
    if (cur[shareId] === tabId) return
    set({ lastTabPerShare: { ...cur, [shareId]: tabId } })
  },

  setActiveProject: (id) => {
    const state = get()
    // Save current folder's split views before switching
    const svps = { ...state.splitViewsPerFolder }
    if (state.activeFolderId) {
      svps[state.activeFolderId] = { splitViews: state.splitViews, activeSplitViewId: state.activeSplitViewId }
    }
    const projectFolders = id ? (state.folders[id] || []) : []
    const lastFolderId = id ? state.lastFolderPerProject[id] : null
    const restoredFolder = lastFolderId ? projectFolders.find(s => s.id === lastFolderId) : null
    const folder = restoredFolder || projectFolders[0] || null
    const folderId = folder?.id || null
    const folderTabs = folderId ? state.tabs.filter((t) => t.folderId === folderId) : []
    // Pick the tab to land on: prefer the folder's last-active tab if
    // it's still in the folder's tab list, otherwise fall back to the
    // first tab of the folder.
    const lastTabId = folderId ? state.lastTabPerFolder[folderId] : null
    const restoredTab = lastTabId ? folderTabs.find((t) => t.id === lastTabId) : null
    const targetTabId = restoredTab?.id || folderTabs[0]?.id || null
    // Restore target folder's split views
    const restored = folderId ? svps[folderId] : null
    // Auto-clear unseen marker for the folder we're switching to
    let unseenFolders = state.unseenFolders
    if (folderId && unseenFolders[folderId]) {
      const { [folderId]: _, ...rest } = unseenFolders
      unseenFolders = rest
    }
    set({
      activeProjectId: id,
      activeFolderId: folderId,
      activeTabId: targetTabId,
      splitViews: restored?.splitViews || [],
      activeSplitViewId: restored?.activeSplitViewId || null,
      selectedTabIds: [],
      splitViewsPerFolder: svps,
      unseenFolders,
    })
  },
  collapseSidebar: () => set({ sidebarState: 'collapsed' }),
  hideSidebar: () => set({ sidebarState: 'hidden' }),
  expandSidebar: () => set({ sidebarState: 'expanded' }),
  showSidebar: () => set({ sidebarState: 'collapsed' }),
  setThemeChoice: (choice) => set({ themeChoice: choice }),

  // Folders
  setActiveFolder: (id) => {
    const state = get()
    // Save current folder's split views before switching
    const svps = { ...state.splitViewsPerFolder }
    if (state.activeFolderId) {
      svps[state.activeFolderId] = { splitViews: state.splitViews, activeSplitViewId: state.activeSplitViewId }
    }
    const folderTabs = id ? state.tabs.filter((t) => t.folderId === id) : []
    const projectId = state.activeProjectId
    const lastFolderPerProject = projectId && id
      ? { ...state.lastFolderPerProject, [projectId]: id }
      : state.lastFolderPerProject
    // Pick the tab to land on: prefer the folder's last-active tab if
    // it's still in the folder's tab list, otherwise fall back to the
    // first tab of the folder.
    const lastTabId = id ? state.lastTabPerFolder[id] : null
    const restoredTab = lastTabId ? folderTabs.find((t) => t.id === lastTabId) : null
    const targetTabId = restoredTab?.id || folderTabs[0]?.id || null
    // Restore target folder's split views
    const restored = id ? svps[id] : null
    // Auto-clear unseen marker for the folder we're switching to.
    // Only clear 'active' (completed) — 'waiting' (authorize) persists
    // so the blue dot stays visible until the folder is no longer waiting.
    let unseenFolders = state.unseenFolders
    if (id && unseenFolders[id] === 'active') {
      const { [id]: _, ...rest } = unseenFolders
      unseenFolders = rest
    }
    set({
      activeFolderId: id,
      activeTabId: targetTabId,
      lastFolderPerProject,
      splitViews: restored?.splitViews || [],
      activeSplitViewId: restored?.activeSplitViewId || null,
      selectedTabIds: [],
      splitViewsPerFolder: svps,
      unseenFolders,
      // Navigating to a folder always leaves the Project Manager full-screen chat.
      activePmProjectId: null,
    })
  },

  // Tabs
  addTab: (tab) =>
    set((s) => {
      // Prevent duplicate tabs by ID
      if (s.tabs.some((t) => t.id === tab.id)) {
        // Same "activating a tab leaves the PM full-screen chat" rule as the
        // main path below — keep both early-return and main path consistent.
        return { activeTabId: tab.id, activePmProjectId: null }
      }
      // Hard cap of 100 tabs per (project, folder) — this matches the
      // filtered set the TabBar shows. Past 100 the bar can't render
      // them sensibly even at icon-only width, so we refuse silently.
      const sameContextCount = s.tabs.filter(
        (t) => t.projectId === tab.projectId && t.folderId === tab.folderId,
      ).length
      if (sameContextCount >= 100) {
        return s
      }
      // Only add to split view if the user is currently viewing it
      let splitViews = s.splitViews
      const asv = getASV(s)
      const isViewingSplit = asv && asv.panels.some((p) => p.tabIds.includes(s.activeTabId || ''))
      if (asv && isViewingSplit) {
        if (asv.panels.length < 4) {
          // Add as a new panel and upgrade layout
          const newPanelId = crypto.randomUUID()
          const panels = [...asv.panels, { id: newPanelId, tabIds: [tab.id], activeTabId: tab.id }]
          const layout = pickLayout(panels.length, s.lastLayoutPerCount, asv.layout)
          splitViews = mapSV(splitViews, asv.id, (sv) => ({
            ...sv, panels, layout, activePanelId: newPanelId,
          }))
        }
        // If already 4 panels, tab is added outside the split view (no split view change)
      }
      return {
        tabs: [...s.tabs, tab],
        activeTabId: tab.id,
        splitViews,
        // Activating any tab always leaves the Project Manager full-screen chat.
        activePmProjectId: null,
      }
    }),
  replaceTab: (oldId, newTab) =>
    set((s) => {
      // Replace old tab with new tab in the tabs array
      const newTabs = s.tabs.filter((t) => t.id !== oldId)
      newTabs.push(newTab)
      // Swap the tab ID in any split view panel
      const splitViews = s.splitViews.map((sv) => ({
        ...sv,
        panels: sv.panels.map((p) => {
          if (!p.tabIds.includes(oldId)) return p
          return { ...p, tabIds: p.tabIds.map((id) => id === oldId ? newTab.id : id), activeTabId: p.activeTabId === oldId ? newTab.id : p.activeTabId }
        }),
      }))
      return { tabs: newTabs, activeTabId: newTab.id, splitViews }
    }),
  removeTab: (id) =>
    set((s) => {
      const idx = s.tabs.findIndex((t) => t.id === id)
      const closedTab = s.tabs[idx]
      const newTabs = s.tabs.filter((t) => t.id !== id)
      let newActiveId = s.activeTabId

      // Clean up across ALL split views
      let splitViews = s.splitViews.map((sv) => {
        let panels = sv.panels.map((p) => {
          if (!p.tabIds.includes(id)) return p
          const newTabIds = p.tabIds.filter((tid) => tid !== id)
          return { ...p, tabIds: newTabIds, activeTabId: p.activeTabId === id ? (newTabIds[0] || null) : p.activeTabId }
        }).filter((p) => p.tabIds.length > 0)
        if (panels.length <= 1) return null // dissolve this split view
        return { ...sv, panels, layout: fitLayout(sv.layout, panels.length, s.lastLayoutPerCount) }
      }).filter(Boolean) as SplitViewState[]
      let activeSplitViewId = s.activeSplitViewId
      // If active SV was dissolved, clear it
      if (activeSplitViewId && !splitViews.find((sv) => sv.id === activeSplitViewId)) {
        activeSplitViewId = null
      }

      if (s.activeTabId === id) {
        const activeSV = activeSplitViewId ? splitViews.find((sv) => sv.id === activeSplitViewId) : null
        if (activeSV) {
          const ap = activeSV.panels.find((p) => p.id === activeSV.activePanelId) || activeSV.panels[0]
          newActiveId = ap?.activeTabId || null
        } else {
          const folderTabs = newTabs.filter((t) => t.folderId === closedTab?.folderId)
          if (folderTabs.length > 0) {
            newActiveId = folderTabs[folderTabs.length - 1]?.id ?? null
          } else {
            // Global tab closed — fall back to the active folder's last tab
            const sid = s.activeFolderId
            const fallbackTabs = sid ? newTabs.filter((t) => t.folderId === sid) : []
            const lastTabId = sid ? s.lastTabPerFolder[sid] : null
            const restoredTab = lastTabId ? fallbackTabs.find((t) => t.id === lastTabId) : null
            newActiveId = restoredTab?.id || fallbackTabs[fallbackTabs.length - 1]?.id || null
          }
        }
      }

      // Clear the lastTabPerFolder entry if it pointed at this tab,
      // so a future setActiveFolder doesn't try to restore a deleted tab.
      let lastTabPerFolder = s.lastTabPerFolder
      if (closedTab?.folderId && lastTabPerFolder[closedTab.folderId] === id) {
        const { [closedTab.folderId]: _removed, ...rest } = lastTabPerFolder
        lastTabPerFolder = rest
      }

      // If the closed tab was the last-remembered Basecamp tab, fall
      // back to any OTHER Basecamp tab still open, else null. The bell
      // click handler re-queries this on every click so it self-heals
      // as soon as the user opens another Basecamp tab.
      let lastBasecampTabId = s.lastBasecampTabId
      if (lastBasecampTabId === id) {
        const fallback = newTabs.find((t) => t.type === 'basecamp')
        lastBasecampTabId = fallback?.id || null
      }

      // Clean up discovery draft if closing a discovery tab
      let discoveryDrafts = s.discoveryDrafts
      if (closedTab?.type === 'discovery' && closedTab.folderId && discoveryDrafts[closedTab.folderId]) {
        const { [closedTab.folderId]: _, ...rest } = discoveryDrafts
        discoveryDrafts = rest
      }

      dispatchResize()
      return { tabs: newTabs, activeTabId: newActiveId, splitViews, activeSplitViewId, lastTabPerFolder, lastBasecampTabId, discoveryDrafts }
    }),
  setActiveTab: (id) => {
    const state = get()
    // Record this tab as the last-active tab for its folder, so a later
    // setActiveFolder / setActiveProject restores it instead of jumping
    // back to the folder's first tab. Look up the tab's owning folder
    // by id and only record if we find it (defensive against stale ids).
    const tab = state.tabs.find((t) => t.id === id)
    const lastTabPerFolder = (tab && tab.folderId)
      ? { ...state.lastTabPerFolder, [tab.folderId]: id }
      : state.lastTabPerFolder

    // Remember the last Basecamp tab the user actually viewed — the
    // bell popover's click handler uses this to decide where to land
    // when multiple Basecamp tabs exist. "Open the one I was last
    // looking at" is the intuitive behaviour.
    const lastBasecampTabId = tab && tab.type === 'basecamp' ? id : state.lastBasecampTabId

    // Check if the tab is inside a split view
    const containingSV = state.splitViews.find((sv) => sv.panels.some((p) => p.tabIds.includes(id)))
    if (containingSV) {
      // Tab is in a split view — keep viewing that split, update the panel's active tab
      const wasInSplit = !!state.activeSplitViewId
      const panel = containingSV.panels.find((p) => p.tabIds.includes(id))
      set({
        activeTabId: id,
        selectionAnchorId: id,
        activeSplitViewId: containingSV.id,
        lastTabPerFolder,
        lastBasecampTabId,
        // Activating any tab always leaves the Project Manager full-screen chat.
        activePmProjectId: null,
        splitViews: mapSV(state.splitViews, containingSV.id, (sv) => ({
          ...sv,
          panels: sv.panels.map((p) => p.id === panel?.id ? { ...p, activeTabId: id } : p),
          activePanelId: panel?.id || sv.activePanelId,
        })),
      })
      // Trigger resize when entering/re-entering split view so terminals fit
      if (!wasInSplit || state.activeSplitViewId !== containingSV.id) {
        dispatchResize()
      }
    } else {
      // Tab is not in any split view — show it solo but keep split views visible in tab bar
      set({ activeTabId: id, selectionAnchorId: id, lastTabPerFolder, lastBasecampTabId, activePmProjectId: null })
    }
  },
  updateTab: (id, updates) =>
    set((s) => ({
      tabs: s.tabs.map((t) => (t.id === id ? { ...t, ...updates } : t))
    })),

  // Folders
  setFolders: (projectId, folders) =>
    set((s) => ({
      folders: { ...s.folders, [projectId]: folders }
    })),
  addFolder: (folder) =>
    set((s) => ({
      folders: {
        ...s.folders,
        [folder.projectId]: [
          ...(s.folders[folder.projectId] || []),
          folder
        ]
      }
    })),
  updateFolder: (projectId, folderId, updates) =>
    set((s) => ({
      folders: {
        ...s.folders,
        [projectId]: (s.folders[projectId] || []).map((f) =>
          f.id === folderId ? { ...f, ...updates } : f
        )
      }
    })),
  removeFolder: (projectId, folderId) =>
    set((s) => {
      // Strip every per-folder slice so worktree churn over a long run doesn't
      // accumulate orphaned entries (lastTabPerFolder / lastFolderPerProject also
      // persist to disk, so this shrinks the saved settings too).
      const { [folderId]: _d, ...discoveryDrafts } = s.discoveryDrafts
      const { [folderId]: _lt, ...lastTabPerFolder } = s.lastTabPerFolder
      const { [folderId]: _pm, ...pendingMessages } = s.pendingMessages
      const { [folderId]: _mc, ...pendingMcpSelections } = s.pendingMcpSelections
      const { [folderId]: _pp, ...pendingPermissionModes } = s.pendingPermissionModes
      const { [folderId]: _ef, ...pendingEffortLevels } = s.pendingEffortLevels
      const { [folderId]: _un, ...unseenFolders } = s.unseenFolders
      const { [folderId]: _ps, ...pendingSelections } = s.pendingSelections
      const { [folderId]: _sv, ...splitViewsPerFolder } = s.splitViewsPerFolder
      let lastFolderPerProject = s.lastFolderPerProject
      if (lastFolderPerProject[projectId] === folderId) {
        const { [projectId]: _lf, ...rest } = lastFolderPerProject
        lastFolderPerProject = rest
      }
      return {
        folders: {
          ...s.folders,
          [projectId]: (s.folders[projectId] || []).filter(
            (f) => f.id !== folderId
          )
        },
        discoveryDrafts,
        lastTabPerFolder,
        pendingMessages,
        pendingMcpSelections,
        pendingPermissionModes,
        pendingEffortLevels,
        unseenFolders,
        pendingSelections,
        splitViewsPerFolder,
        lastFolderPerProject,
      }
    }),

  // Sidebar Apps
  setSidebarApps: (projectId, apps) => set((s) => ({ sidebarApps: { ...s.sidebarApps, [projectId]: apps } })),
  addSidebarApp: (app) => set((s) => ({ sidebarApps: { ...s.sidebarApps, [app.projectId]: [...(s.sidebarApps[app.projectId] || []), app] } })),
  removeSidebarApp: (projectId, appId) => set((s) => ({ sidebarApps: { ...s.sidebarApps, [projectId]: (s.sidebarApps[projectId] || []).filter(a => a.id !== appId) }, activeSidebarAppId: s.activeSidebarAppId === appId ? null : s.activeSidebarAppId })),
  setActiveSidebarApp: (id) => set({ activeSidebarAppId: id, appPickerProject: null }),
  setAppPickerProject: (projectId) => set({ appPickerProject: projectId }),
  // Codex Sessions
  setCodexSessions: (projectId, sessions) =>
    set((s) => ({ codexSessions: { ...s.codexSessions, [projectId]: sessions } })),
  addCodexSession: (session) =>
    set((s) => ({
      codexSessions: {
        ...s.codexSessions,
        [session.projectId]: [...(s.codexSessions[session.projectId] || []), session]
      }
    })),
  updateCodexSession: (projectId, sessionId, updates) =>
    set((s) => ({
      codexSessions: {
        ...s.codexSessions,
        [projectId]: (s.codexSessions[projectId] || []).map((sess) =>
          sess.id === sessionId ? { ...sess, ...updates } : sess
        )
      }
    })),
  removeCodexSession: (projectId, sessionId) =>
    set((s) => ({
      codexSessions: {
        ...s.codexSessions,
        [projectId]: (s.codexSessions[projectId] || []).filter(
          (sess) => sess.id !== sessionId
        )
      }
    })),

  // Grok Sessions
  setGrokSessions: (projectId, sessions) =>
    set((s) => ({ grokSessions: { ...s.grokSessions, [projectId]: sessions } })),
  addGrokSession: (session) =>
    set((s) => ({
      grokSessions: {
        ...s.grokSessions,
        [session.projectId]: [...(s.grokSessions[session.projectId] || []), session]
      }
    })),
  updateGrokSession: (projectId, sessionId, updates) =>
    set((s) => ({
      grokSessions: {
        ...s.grokSessions,
        [projectId]: (s.grokSessions[projectId] || []).map((sess) =>
          sess.id === sessionId ? { ...sess, ...updates } : sess
        )
      }
    })),
  removeGrokSession: (projectId, sessionId) =>
    set((s) => ({
      grokSessions: {
        ...s.grokSessions,
        [projectId]: (s.grokSessions[projectId] || []).filter(
          (sess) => sess.id !== sessionId
        )
      }
    })),

  // Gemini Sessions
  setGeminiSessions: (projectId, sessions) =>
    set((s) => ({ geminiSessions: { ...s.geminiSessions, [projectId]: sessions } })),
  addGeminiSession: (session) =>
    set((s) => ({
      geminiSessions: {
        ...s.geminiSessions,
        [session.projectId]: [...(s.geminiSessions[session.projectId] || []), session]
      }
    })),
  updateGeminiSession: (projectId, sessionId, updates) =>
    set((s) => ({
      geminiSessions: {
        ...s.geminiSessions,
        [projectId]: (s.geminiSessions[projectId] || []).map((sess) =>
          sess.id === sessionId ? { ...sess, ...updates } : sess
        )
      }
    })),
  removeGeminiSession: (projectId, sessionId) =>
    set((s) => ({
      geminiSessions: {
        ...s.geminiSessions,
        [projectId]: (s.geminiSessions[projectId] || []).filter(
          (sess) => sess.id !== sessionId
        )
      }
    })),

  // Kimi Sessions
  setKimiSessions: (projectId, sessions) =>
    set((s) => ({ kimiSessions: { ...s.kimiSessions, [projectId]: sessions } })),
  addKimiSession: (session) =>
    set((s) => ({
      kimiSessions: {
        ...s.kimiSessions,
        [session.projectId]: [...(s.kimiSessions[session.projectId] || []), session]
      }
    })),
  updateKimiSession: (projectId, sessionId, updates) =>
    set((s) => ({
      kimiSessions: {
        ...s.kimiSessions,
        [projectId]: (s.kimiSessions[projectId] || []).map((sess) =>
          sess.id === sessionId ? { ...sess, ...updates } : sess
        )
      }
    })),
  removeKimiSession: (projectId, sessionId) =>
    set((s) => ({
      kimiSessions: {
        ...s.kimiSessions,
        [projectId]: (s.kimiSessions[projectId] || []).filter(
          (sess) => sess.id !== sessionId
        )
      }
    })),

  setPtyMapping: (sessionId, ptyId) =>
    set((s) => ({ ptyMap: { ...s.ptyMap, [sessionId]: ptyId } })),
  removePtyMapping: (sessionId) =>
    set((s) => {
      const { [sessionId]: _, ...rest } = s.ptyMap
      return { ptyMap: rest }
    }),

  // Terminals
  setTerminals: (projectId, terminals) =>
    set((s) => ({ terminals: { ...s.terminals, [projectId]: terminals } })),
  addTerminal: (terminal) =>
    set((s) => ({
      terminals: {
        ...s.terminals,
        [terminal.projectId]: [...(s.terminals[terminal.projectId] || []), terminal]
      }
    })),
  updateTerminal: (projectId, terminalId, updates) =>
    set((s) => ({
      terminals: {
        ...s.terminals,
        [projectId]: (s.terminals[projectId] || []).map((t) =>
          t.id === terminalId ? { ...t, ...updates } : t
        )
      }
    })),
  removeTerminal: (projectId, terminalId) =>
    set((s) => ({
      terminals: {
        ...s.terminals,
        [projectId]: (s.terminals[projectId] || []).filter((t) => t.id !== terminalId)
      }
    })),

  // Discovery
  setDiscoveredAgents: (projectId, agents) =>
    set((s) => ({
      discoveredAgents: { ...s.discoveredAgents, [projectId]: agents }
    })),

  // Project Manager
  setPmMessages: (projectId, msgs) =>
    set((s) => ({
      pmChats: { ...s.pmChats, [projectId]: msgs }
    })),
  appendPmChat: (projectId, msg) =>
    set((s) => {
      const existing = s.pmChats[projectId] || []
      const idx = existing.findIndex((m) => m.id === msg.id)
      if (idx === -1) {
        return { pmChats: { ...s.pmChats, [projectId]: [...existing, msg] } }
      }
      // Same id already present (e.g. pmGetState's snapshot racing the live
      // stream, OR the orchestrator re-pushing an action message once its
      // tool result backfills a sessionId) — upsert IN PLACE so order is
      // preserved instead of appending a duplicate.
      const updated = existing.slice()
      updated[idx] = msg
      return { pmChats: { ...s.pmChats, [projectId]: updated } }
    }),
  setPmThinking: (projectId, v) =>
    set((s) => ({
      pmThinking: { ...s.pmThinking, [projectId]: v }
    })),

  setPendingMessage: (folderId, message) =>
    set((s) => {
      if (!message) {
        const { [folderId]: _, ...rest } = s.pendingMessages
        return { pendingMessages: rest }
      }
      return { pendingMessages: { ...s.pendingMessages, [folderId]: message } }
    }),

  setPendingTransferNotice: (sessionId, notice) =>
    set((s) => {
      if (!notice) {
        const { [sessionId]: _, ...rest } = s.pendingTransferNotices
        return { pendingTransferNotices: rest }
      }
      return { pendingTransferNotices: { ...s.pendingTransferNotices, [sessionId]: notice } }
    }),

  setPendingMcpSelection: (folderId, enabled) =>
    set((s) => {
      if (enabled === null) {
        const { [folderId]: _, ...rest } = s.pendingMcpSelections
        return { pendingMcpSelections: rest }
      }
      return { pendingMcpSelections: { ...s.pendingMcpSelections, [folderId]: enabled } }
    }),

  setPendingPermissionMode: (folderId, mode) =>
    set((s) => {
      if (mode === null) {
        const { [folderId]: _, ...rest } = s.pendingPermissionModes
        return { pendingPermissionModes: rest }
      }
      return { pendingPermissionModes: { ...s.pendingPermissionModes, [folderId]: mode } }
    }),

  setPendingEffortLevel: (folderId, effort) =>
    set((s) => {
      if (effort === null) {
        const { [folderId]: _, ...rest } = s.pendingEffortLevels
        return { pendingEffortLevels: rest }
      }
      return { pendingEffortLevels: { ...s.pendingEffortLevels, [folderId]: effort } }
    }),

  setPendingModel: (folderId, model) =>
    set((s) => {
      if (model === null) {
        const { [folderId]: _, ...rest } = s.pendingModels
        return { pendingModels: rest }
      }
      return { pendingModels: { ...s.pendingModels, [folderId]: model } }
    }),

  loadAgentCapabilities: async (force?: boolean) => {
    if (!force && get().agentCapabilities) return
    try {
      const caps = force
        ? await window.api.agentCapabilities.refresh()
        : await window.api.agentCapabilities.get()
      set({ agentCapabilities: caps })
    } catch {
      /* leave as null; pickers fall back to empty/curated */
    }
  },

  setDiscoveryDraft: (folderId, draft) =>
    set((s) => {
      if (!draft) {
        const { [folderId]: _, ...rest } = s.discoveryDrafts
        return { discoveryDrafts: rest }
      }
      return { discoveryDrafts: { ...s.discoveryDrafts, [folderId]: draft } }
    }),

  markFolderUnseen: (folderId, status) =>
    set((s) => {
      // 'waiting' beats 'active' — once a folder needs input, an "active" event
      // shouldn't downgrade the indicator
      if (s.unseenFolders[folderId] === 'waiting' && status === 'active') return s
      if (s.unseenFolders[folderId] === status) return s
      return { unseenFolders: { ...s.unseenFolders, [folderId]: status } }
    }),
  markFolderSeen: (folderId) =>
    set((s) => {
      if (!s.unseenFolders[folderId]) return s
      const { [folderId]: _, ...rest } = s.unseenFolders
      return { unseenFolders: rest }
    }),

  setPendingSelection: (folderId, sel) =>
    set((s) => ({ pendingSelections: { ...s.pendingSelections, [folderId]: sel ? [sel] : null } })),
  setPendingSelections: (folderId, sels) =>
    set((s) => ({ pendingSelections: { ...s.pendingSelections, [folderId]: sels } })),
  addPendingSelection: (folderId, sel) =>
    set((s) => {
      const current = s.pendingSelections[folderId] || []
      // Don't add duplicate files
      if (current.some(existing => existing.file === sel.file && existing.startLine === sel.startLine && existing.endLine === sel.endLine)) return s
      return { pendingSelections: { ...s.pendingSelections, [folderId]: [...current, sel] } }
    }),
  removePendingSelection: (folderId, idx) =>
    set((s) => {
      const current = s.pendingSelections[folderId]
      if (!current) return s
      const updated = current.filter((_, i) => i !== idx)
      return { pendingSelections: { ...s.pendingSelections, [folderId]: updated.length > 0 ? updated : null } }
    }),

  // UI
  reorderTabs: (fromId, toId) =>
    set((s) => {
      const tabs = [...s.tabs]
      const fromIdx = tabs.findIndex((t) => t.id === fromId)
      const toIdx = tabs.findIndex((t) => t.id === toId)
      if (fromIdx < 0 || toIdx < 0) return s
      const [moved] = tabs.splice(fromIdx, 1)
      tabs.splice(toIdx, 0, moved)

      // Sort split view panels to match the new tab order
      const tabIndex = new Map(tabs.map((t, i) => [t.id, i]))
      let svChanged = false
      const splitViews = s.splitViews.map((sv) => {
        // Check if both tabs belong to different panels in this split view
        const fromPanel = sv.panels.findIndex((p) => p.tabIds.includes(fromId))
        const toPanel = sv.panels.findIndex((p) => p.tabIds.includes(toId))
        if (fromPanel < 0 || toPanel < 0 || fromPanel === toPanel) return sv
        // Sort panels by the position of their first tab in the global tab order
        const sorted = [...sv.panels].sort((a, b) => {
          const aMin = Math.min(...a.tabIds.map((id) => tabIndex.get(id) ?? Infinity))
          const bMin = Math.min(...b.tabIds.map((id) => tabIndex.get(id) ?? Infinity))
          return aMin - bMin
        })
        svChanged = true
        return { ...sv, panels: sorted }
      })

      if (svChanged) {
        // Trigger resize so terminals re-fit to their new panel positions
        dispatchResize()
      }
      return svChanged ? { tabs, splitViews } : { tabs }
    }),

  // Reorder top-level items (split view groups and/or individual tabs).
  // fromId/toId can each be a tab ID or a split view ID.
  reorderTopLevel: (fromId, toId) =>
    set((s) => {
      if (fromId === toId) return s
      const fromSv = s.splitViews.find((sv) => sv.id === fromId)
      const toSv = s.splitViews.find((sv) => sv.id === toId)
      const fromTabIds = fromSv
        ? new Set(fromSv.panels.flatMap((p) => p.tabIds))
        : new Set([fromId])
      const toTabIds = toSv
        ? new Set(toSv.panels.flatMap((p) => p.tabIds))
        : new Set([toId])
      // Determine direction from original positions
      let firstFromIdx = -1, firstToIdx = -1
      for (let i = 0; i < s.tabs.length; i++) {
        if (firstFromIdx < 0 && fromTabIds.has(s.tabs[i].id)) firstFromIdx = i
        if (firstToIdx < 0 && toTabIds.has(s.tabs[i].id)) firstToIdx = i
      }
      if (firstFromIdx < 0 || firstToIdx < 0) return s
      const movingRight = firstFromIdx < firstToIdx
      const toMove = s.tabs.filter((t) => fromTabIds.has(t.id))
      const remaining = s.tabs.filter((t) => !fromTabIds.has(t.id))
      let insertIdx = -1
      if (movingRight) {
        // Insert AFTER the last target tab
        for (let i = remaining.length - 1; i >= 0; i--) {
          if (toTabIds.has(remaining[i].id)) { insertIdx = i + 1; break }
        }
      } else {
        // Insert BEFORE the first target tab
        insertIdx = remaining.findIndex((t) => toTabIds.has(t.id))
      }
      if (insertIdx < 0) return s
      remaining.splice(insertIdx, 0, ...toMove)
      return { tabs: remaining }
    }),

  reorderTopLevelGroup: (fromIds, toId) =>
    set((s) => {
      if (fromIds.length === 0) return s
      const splitTabIds = new Set(s.splitViews.flatMap((sv) => sv.panels.flatMap((p) => p.tabIds)))
      const movableIds = new Set(fromIds.filter((id) => !splitTabIds.has(id)))
      if (movableIds.size === 0 || movableIds.has(toId)) return s

      const toSv = s.splitViews.find((sv) => sv.id === toId)
      const toTabIds = toSv
        ? new Set(toSv.panels.flatMap((p) => p.tabIds))
        : new Set([toId])

      let firstFromIdx = -1, firstToIdx = -1
      for (let i = 0; i < s.tabs.length; i++) {
        if (firstFromIdx < 0 && movableIds.has(s.tabs[i].id)) firstFromIdx = i
        if (firstToIdx < 0 && toTabIds.has(s.tabs[i].id)) firstToIdx = i
      }
      if (firstFromIdx < 0 || firstToIdx < 0) return s
      const movingRight = firstFromIdx < firstToIdx

      const toMove = s.tabs.filter((t) => movableIds.has(t.id))
      const remaining = s.tabs.filter((t) => !movableIds.has(t.id))
      let insertIdx = -1
      if (movingRight) {
        for (let i = remaining.length - 1; i >= 0; i--) {
          if (toTabIds.has(remaining[i].id)) { insertIdx = i + 1; break }
        }
      } else {
        insertIdx = remaining.findIndex((t) => toTabIds.has(t.id))
      }
      if (insertIdx < 0) return s
      remaining.splice(insertIdx, 0, ...toMove)
      return { tabs: remaining }
    }),

  reorderProjects: (fromId, toId) =>
    set((s) => {
      const projects = [...s.projects]
      const fromIdx = projects.findIndex((p) => p.id === fromId)
      const toIdx = projects.findIndex((p) => p.id === toId)
      if (fromIdx < 0 || toIdx < 0) return s
      const [moved] = projects.splice(fromIdx, 1)
      projects.splice(toIdx, 0, moved)
      return { projects }
    }),

  reorderFolders: (projectId, fromId, toId) =>
    set((s) => {
      const folders = [...(s.folders[projectId] || [])]
      const fromIdx = folders.findIndex((f) => f.id === fromId)
      const toIdx = folders.findIndex((f) => f.id === toId)
      if (fromIdx < 0 || toIdx < 0) return s
      const [moved] = folders.splice(fromIdx, 1)
      folders.splice(toIdx, 0, moved)
      return { folders: { ...s.folders, [projectId]: folders } }
    }),

  // Split View
  toggleTabSelection: (tabId) =>
    set((s) => {
      let selected = [...s.selectedTabIds]
      // Auto-include active tab on first ctrl/cmd+click
      if (selected.length === 0 && s.activeTabId && s.activeTabId !== tabId) {
        selected.push(s.activeTabId)
      }
      if (selected.includes(tabId)) {
        selected = selected.filter((id) => id !== tabId)
      } else {
        selected.push(tabId)
      }
      // Ctrl/cmd+click sets the new anchor for subsequent shift+click ranges
      return { selectedTabIds: selected, selectionAnchorId: tabId }
    }),
  selectTabRange: (tabIds) => set({ selectedTabIds: tabIds }),
  clearTabSelection: () => set({ selectedTabIds: [], selectionAnchorId: null }),

  addToSplitView: (tabIds, targetSvId) => {
    const state = get()
    // The Apps gallery is a launcher — never a valid split panel participant.
    // Filter it out here as a last line of defense; the TabBar menu already
    // hides the split options for apps tabs.
    tabIds = tabIds.filter((id) => {
      const t = state.tabs.find((tab) => tab.id === id)
      return t && t.type !== 'apps'
    })
    if (tabIds.length === 0) return
    if (targetSvId) {
      // Add to existing split view
      const sv = state.splitViews.find((s) => s.id === targetSvId)
      if (!sv) return
      // Remove from any other split views first
      let splitViews = state.splitViews.map((s) => {
        if (s.id === targetSvId) return s
        let panels = s.panels.map((p) => ({ ...p, tabIds: p.tabIds.filter((id) => !tabIds.includes(id)) }))
          .map((p) => ({ ...p, activeTabId: p.tabIds.includes(p.activeTabId || '') ? p.activeTabId : (p.tabIds[0] || null) }))
          .filter((p) => p.tabIds.length > 0)
        if (panels.length <= 1) return null
        return { ...s, panels, layout: fitLayout(s.layout, panels.length, state.lastLayoutPerCount) }
      }).filter(Boolean) as SplitViewState[]
      // Add tabs as new panels in target (max 4 panels, 1 tab each)
      const target = splitViews.find((s) => s.id === targetSvId)!
      if (target.panels.length >= 4) return // already at max
      const allowedCount = 4 - target.panels.length
      const limitedTabIds = tabIds.slice(0, allowedCount)
      if (limitedTabIds.length === 0) return
      const newPanels = limitedTabIds.map((id) => ({ id: crypto.randomUUID(), tabIds: [id], activeTabId: id }))
      const lastNewPanelId = newPanels[newPanels.length - 1].id
      const panels = [...target.panels, ...newPanels]
      const layout = pickLayout(panels.length, state.lastLayoutPerCount, target.layout)
      splitViews = mapSV(splitViews, targetSvId, (sv) => ({ ...sv, panels, layout, activePanelId: lastNewPanelId }))
      set({ splitViews, activeSplitViewId: targetSvId, activeTabId: limitedTabIds[0] || state.activeTabId, selectedTabIds: [] })
      dispatchResize()
    } else {
      // Create new split view (max 4 tabs)
      const limitedIds = tabIds.slice(0, 4)
      const panels: SplitPanel[] = limitedIds.map((id) => ({ id: crypto.randomUUID(), tabIds: [id], activeTabId: id }))
      if (panels.length === 1 && state.activeTabId && !limitedIds.includes(state.activeTabId)) {
        panels.unshift({ id: crypto.randomUUID(), tabIds: [state.activeTabId], activeTabId: state.activeTabId })
      }
      if (panels.length < 2) return
      const svId = crypto.randomUUID()
      const newLayout = pickLayout(panels.length, state.lastLayoutPerCount)
      const saved = state.lastRatiosPerLayout[newLayout]
      const defaultVr = newLayout === '3-col' ? 33 : 50
      const defaultHr = newLayout === '3-col' ? 66 : 50
      const newSV: SplitViewState = {
        id: svId, layout: newLayout, panels,
        activePanelId: panels[panels.length - 1].id,
        horizontalRatio: saved?.hr ?? defaultHr,
        verticalRatio: saved?.vr ?? defaultVr,
        name: `Split View${state.splitViews.length > 0 ? ` ${state.splitViews.length + 1}` : ''}`,
      }
      set({
        splitViews: [...state.splitViews, newSV],
        activeSplitViewId: svId,
        activeTabId: tabIds[0] || state.activeTabId,
        selectedTabIds: [],
      })
      dispatchResize()
    }
  },

  closeSplitView: (id) => {
    const state = get()
    const svId = id || state.activeSplitViewId
    if (!svId) return
    const sv = state.splitViews.find((s) => s.id === svId)
    const activePanel = sv?.panels.find((p) => p.id === sv?.activePanelId) || sv?.panels[0]
    set({
      splitViews: state.splitViews.filter((s) => s.id !== svId),
      activeSplitViewId: state.activeSplitViewId === svId ? null : state.activeSplitViewId,
      activeTabId: state.activeSplitViewId === svId ? (activePanel?.activeTabId || state.activeTabId) : state.activeTabId,
      selectedTabIds: [],
    })
    dispatchResize()
  },

  setActivePanelId: (panelId) => {
    const state = get()
    const sv = getASV(state)
    if (!sv) return
    const panel = sv.panels.find((p) => p.id === panelId)
    set({
      splitViews: mapSV(state.splitViews, sv.id, (s) => ({ ...s, activePanelId: panelId })),
      activeTabId: panel?.activeTabId || state.activeTabId,
    })
  },

  setPanelActiveTab: (panelId, tabId) => {
    const state = get()
    const sv = getASV(state)
    if (!sv) return
    set({
      splitViews: mapSV(state.splitViews, sv.id, (s) => ({
        ...s,
        panels: s.panels.map((p) => p.id === panelId ? { ...p, activeTabId: tabId } : p),
        activePanelId: panelId,
      })),
      activeTabId: tabId,
    })
  },

  updateSplitRatio: (type, ratio) => {
    const state = get()
    const sv = getASV(state)
    if (!sv) return
    const newVr = type === 'vertical' ? ratio : sv.verticalRatio
    const newHr = type === 'horizontal' ? ratio : sv.horizontalRatio
    set({
      splitViews: mapSV(state.splitViews, sv.id, (s) => ({
        ...s,
        ...(type === 'horizontal' ? { horizontalRatio: ratio } : { verticalRatio: ratio }),
      })),
    })
    // Persist ratios per layout type if setting enabled (async, non-blocking)
    window.api.getSetting('rememberSplitSizes').then((enabled: unknown) => {
      if (!enabled) return
      const updated = { ...get().lastRatiosPerLayout, [sv.layout]: { vr: newVr, hr: newHr } }
      set({ lastRatiosPerLayout: updated })
      window.api.setSetting('lastRatiosPerLayout', updated)
    })
  },

  changeSplitLayout: (layout) => {
    const state = get()
    const sv = getASV(state)
    if (!sv) return
    const count = sv.panels.length
    // Only save preference if layout is valid for this panel count
    const maxPanels: Record<string, number> = { '2-col': 2, '2-row': 2, '3-col': 3, '2-col-1-bottom': 3, '1-top-2-col-bottom': 3, '2x2': 4 }
    const validForCount = !maxPanels[layout] || maxPanels[layout] >= count
    // Use saved ratios if available, otherwise sensible defaults
    const saved = state.lastRatiosPerLayout[layout]
    const defaultVr = layout === '3-col' ? 33 : 50
    const defaultHr = layout === '3-col' ? 66 : 50
    const ratioOverrides = {
      verticalRatio: saved?.vr ?? defaultVr,
      horizontalRatio: saved?.hr ?? defaultHr,
    }
    set({
      splitViews: mapSV(state.splitViews, sv.id, (s) => ({ ...s, layout, ...ratioOverrides })),
      lastLayoutPerCount: validForCount
        ? { ...state.lastLayoutPerCount, [count]: layout }
        : state.lastLayoutPerCount,
    })
    dispatchResize()
  },

  moveTabToPanel: (tabId, targetPanelId) => {
    const state = get()
    const sv = getASV(state)
    if (!sv) return
    let panels: SplitPanel[] = sv.panels.map((p) => ({
      ...p, tabIds: p.tabIds.filter((id) => id !== tabId),
    })).map((p) => ({
      ...p, activeTabId: p.tabIds.includes(p.activeTabId || '') ? p.activeTabId : (p.tabIds[0] || null),
    }))
    panels = panels.map((p) => p.id !== targetPanelId ? p : { ...p, tabIds: [tabId], activeTabId: tabId })
    panels = panels.filter((p) => p.tabIds.length > 0)
    if (panels.length <= 1) {
      set({ splitViews: state.splitViews.filter((s) => s.id !== sv.id), activeSplitViewId: null, activeTabId: tabId })
    } else {
      set({
        splitViews: mapSV(state.splitViews, sv.id, (s) => ({ ...s, panels, layout: fitLayout(s.layout, panels.length, state.lastLayoutPerCount), activePanelId: targetPanelId })),
        activeTabId: tabId,
      })
    }
    dispatchResize()
  },

  removeFromSplitPanel: (tabId) => {
    const state = get()
    // Find which split view contains this tab
    const sv = state.splitViews.find((s) => s.panels.some((p) => p.tabIds.includes(tabId)))
    if (!sv) return
    let panels: SplitPanel[] = sv.panels.map((p) => {
      if (!p.tabIds.includes(tabId)) return p
      const newTabIds = p.tabIds.filter((id) => id !== tabId)
      return { ...p, tabIds: newTabIds, activeTabId: p.activeTabId === tabId ? (newTabIds[0] || null) : p.activeTabId }
    }).filter((p) => p.tabIds.length > 0)
    if (panels.length <= 1) {
      set({ splitViews: state.splitViews.filter((s) => s.id !== sv.id), activeSplitViewId: state.activeSplitViewId === sv.id ? null : state.activeSplitViewId })
    } else {
      set({ splitViews: mapSV(state.splitViews, sv.id, (s) => ({ ...s, panels, layout: fitLayout(s.layout, panels.length, state.lastLayoutPerCount) })) })
    }
    dispatchResize()
  },

  reorderSplitPanels: (svId, fromTabId, toTabId) => {
    const state = get()
    const sv = state.splitViews.find((s) => s.id === svId)
    if (!sv) return
    const fromIdx = sv.panels.findIndex((p) => p.tabIds.includes(fromTabId))
    const toIdx = sv.panels.findIndex((p) => p.tabIds.includes(toTabId))
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return
    const panels = [...sv.panels]
    const [moved] = panels.splice(fromIdx, 1)
    panels.splice(toIdx, 0, moved)
    set({ splitViews: mapSV(state.splitViews, svId, (s) => ({ ...s, panels })) })
  },

  renameSplitView: (name, id) => {
    const state = get()
    const svId = id || state.activeSplitViewId
    if (svId) set({ splitViews: mapSV(state.splitViews, svId, (s) => ({ ...s, name })) })
  },
  activateSplitView: (id) => {
    const state = get()
    const svId = id || state.activeSplitViewId
    const sv = svId ? state.splitViews.find((s) => s.id === svId) : null
    if (!sv) return
    const panel = sv.panels.find((p) => p.id === sv.activePanelId) || sv.panels[0]
    set({
      activeSplitViewId: sv.id,
      activeTabId: panel?.activeTabId || null,
    })
    // Trigger resize so terminals re-fit to their panel dimensions
    dispatchResize()
  },

  setShowProjectDialog: (show) => set({ showProjectDialog: show }),

  setPendingProjectPrefill: (value) => set({ pendingProjectPrefill: value }),

  setGlobalSearchOpen: (open) => set({ globalSearchOpen: open }),

  setGlobalSearchPendingMatch: (sessionId, value) =>
    set((s) => {
      const next = { ...s.globalSearchPendingMatch }
      if (value === null) delete next[sessionId]
      else next[sessionId] = value
      return { globalSearchPendingMatch: next }
    }),

  // ── Favorite apps ──────────────────────────────────────────────────────
  // The hard cap (10) is enforced here in the reducer rather than the UI so
  // any caller path (gallery, future drag-drop, IPC restore) is guarded.
  setFavoriteApps: (ids) => set({ favoriteApps: ids.slice(0, 10) }),
  toggleFavoriteApp: (id) =>
    set((s) => {
      if (s.favoriteApps.includes(id)) {
        return { favoriteApps: s.favoriteApps.filter((x) => x !== id) }
      }
      // At cap → no-op (UI also disables the star, but this is the source of truth).
      if (s.favoriteApps.length >= 10) return { favoriteApps: s.favoriteApps }
      return { favoriteApps: [...s.favoriteApps, id] }
    }),

  // ── AiTables row highlight bridge ──────────────────────────────────────
  // ClaudeTerminal.tsx fires this when the user clicks a +Ai Table row chip;
  // AiTablesView watches it and dispatches the highlight inside the webview.
  // Bumping the timestamp on every request guarantees a state change so the
  // effect re-runs even if the same row is clicked twice.
  requestAitablesHighlight: (rowId, tabId, url) =>
    set({ aitablesHighlightRequest: { rowId, tabId, url, ts: Date.now() } }),

  setAitablesRowUrl: (rowId, url) =>
    set((s) => ({ aitablesRowUrls: { ...s.aitablesRowUrls, [rowId]: url } })),

  // ── AiBoards Konva-shape highlight bridge ──────────────────────────────
  // Same shape as the AiTables variant. ClaudeTerminal.tsx fires this when the
  // user clicks an "+Ai Board capture" chip; AiBoardsView watches it and
  // re-attaches a Konva Transformer to the matching shapes for ~3s.
  requestAiboardsHighlight: (elementIds, tabId, url) =>
    set({ aiboardsHighlightRequest: { elementIds, tabId, url, ts: Date.now() } }),

  setAiboardsCaptureElements: (filePath, elementIds) =>
    set((s) => ({ aiboardsCaptureElements: { ...s.aiboardsCaptureElements, [filePath]: elementIds } })),

  setAiboardsCaptureUrl: (filePath, url) =>
    set((s) => ({ aiboardsCaptureUrls: { ...s.aiboardsCaptureUrls, [filePath]: url } })),

  // ── Basecamp tab navigation bridge ──────────────────────────────────────
  // ClaudeTerminal.tsx fires this when the user clicks a Basecamp chip;
  // BasecampView watches it and calls wv.loadURL.
  requestBasecampNavigate: (url, tabId) =>
    set({ basecampNavigateRequest: { url, tabId, ts: Date.now() } }),

  // ── In-app notifications (TitleBar bell) ───────────────────────────────
  // Upserts merge by id (source:kind:refId?) so repeated events /
  // repeated scans just update the count + updatedTs in place rather
  // than stacking duplicate entries. firstSeenTs is preserved for
  // stable "oldest first" sorting, and the `read` flag is cleared on
  // every count INCREASE (new activity arrived) but persists across
  // no-op updates (same count re-scanned).
  upsertAppNotification: (incoming) =>
    set((s) => {
      const existing = s.appNotifications.find((n) => n.id === incoming.id)
      if (!existing) {
        // Cap to the newest 200. ids embed sessionId so each new session appends a
        // fresh entry that never dedupes; without a cap this grows all session.
        const next = [...s.appNotifications, incoming]
        return { appNotifications: next.length > 200 ? next.slice(-200) : next }
      }
      const incCount = incoming.count ?? 0
      const exCount = existing.count ?? 0
      const countIncreased = incCount > exCount
      const merged: AppNotification = {
        ...existing,
        title: incoming.title,
        body: incoming.body ?? existing.body,
        count: incoming.count ?? existing.count,
        url: incoming.url ?? existing.url,
        tabId: incoming.tabId ?? existing.tabId,
        sessionId: incoming.sessionId ?? existing.sessionId,
        updatedTs: incoming.updatedTs,
        read: countIncreased ? false : existing.read
      }
      return {
        appNotifications: s.appNotifications.map((n) =>
          n.id === incoming.id ? merged : n
        )
      }
    }),

  removeAppNotification: (id) =>
    set((s) => ({ appNotifications: s.appNotifications.filter((n) => n.id !== id) })),

  markAppNotificationRead: (id) =>
    set((s) => ({
      appNotifications: s.appNotifications.map((n) =>
        n.id === id ? { ...n, read: true } : n
      )
    })),

  markAllAppNotificationsRead: () =>
    set((s) => ({ appNotifications: s.appNotifications.map((n) => ({ ...n, read: true })) })),

  clearAppNotifications: () => set({ appNotifications: [] }),

  setNotificationPrefs: (prefs) => set({ notificationPrefs: prefs }),

  setNotificationPref: (source, destination, enabled) =>
    set((s) => ({
      notificationPrefs: {
        ...s.notificationPrefs,
        [source]: { ...s.notificationPrefs[source], [destination]: enabled }
      }
    })),

  setVoiceSettings: (settings) => {
    set({ voiceSettings: settings })
    void window.api.setSetting('voiceSettings', settings)
  },

  voiceAddProfile: (profile) => {
    const id = `vp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`
    const next: VoiceProfile = { id, name: profile.name.trim() || 'Untitled', prompt: profile.prompt }
    set((s) => {
      const updated: VoiceSettings = { ...s.voiceSettings, profiles: [...s.voiceSettings.profiles, next] }
      void window.api.setSetting('voiceSettings', updated)
      return { voiceSettings: updated }
    })
    return id
  },

  voiceUpdateProfile: (id, patch) =>
    set((s) => {
      // Raw is read-only — name/prompt cannot change.
      if (id === RAW_VOICE_PROFILE.id) return s
      const profiles = s.voiceSettings.profiles.map((p) =>
        p.id === id ? { ...p, ...patch } : p
      )
      const updated: VoiceSettings = { ...s.voiceSettings, profiles }
      void window.api.setSetting('voiceSettings', updated)
      return { voiceSettings: updated }
    }),

  voiceDeleteProfile: (id) =>
    set((s) => {
      // Raw is non-deletable.
      if (id === RAW_VOICE_PROFILE.id) return s
      const profiles = s.voiceSettings.profiles.filter((p) => p.id !== id)
      // If the deleted profile was referenced anywhere, fall back to Raw.
      const defaultProfileId = s.voiceSettings.defaultProfileId === id ? RAW_VOICE_PROFILE.id : s.voiceSettings.defaultProfileId
      const perApp = { ...s.voiceSettings.perApp }
      for (const k of Object.keys(perApp) as VoiceAppKey[]) {
        if (perApp[k] === id) delete perApp[k]
      }
      const updated: VoiceSettings = { profiles, defaultProfileId, perApp }
      void window.api.setSetting('voiceSettings', updated)
      return { voiceSettings: updated }
    }),

  voiceSetDefault: (id) =>
    set((s) => {
      const exists = s.voiceSettings.profiles.some((p) => p.id === id)
      if (!exists) return s
      const updated: VoiceSettings = { ...s.voiceSettings, defaultProfileId: id }
      void window.api.setSetting('voiceSettings', updated)
      return { voiceSettings: updated }
    }),

  voiceSetPerApp: (appKey, profileId) =>
    set((s) => {
      const exists = s.voiceSettings.profiles.some((p) => p.id === profileId)
      if (!exists) return s
      const perApp: VoiceSettings['perApp'] = { ...s.voiceSettings.perApp, [appKey]: profileId }
      const updated: VoiceSettings = { ...s.voiceSettings, perApp }
      void window.api.setSetting('voiceSettings', updated)
      return { voiceSettings: updated }
    }),

  voiceClearPerApp: (appKey) =>
    set((s) => {
      const perApp = { ...s.voiceSettings.perApp }
      delete perApp[appKey]
      const updated: VoiceSettings = { ...s.voiceSettings, perApp }
      void window.api.setSetting('voiceSettings', updated)
      return { voiceSettings: updated }
    }),

  voiceSetKeybind: (accel) =>
    set((s) => {
      const updated: VoiceSettings = { ...s.voiceSettings, keybind: accel }
      void window.api.setSetting('voiceSettings', updated)
      return { voiceSettings: updated }
    }),

  toggleChat: () => set((s) => {
    const next = !s.chatOpen
    void window.api.setSetting('chatOpen', next)
    return { chatOpen: next }
  }),
  setChatWidth: (width) => set({ chatWidth: Math.max(350, Math.min(800, width)) }),
  openChatProvider: (provider) => set((s) => {
    const already = s.chatProviders.includes(provider)
    const next = already ? s.chatProviders : [...s.chatProviders, provider]
    if (!already) void window.api.setSetting('chatProviders', next)
    void window.api.setSetting('activeChatProvider', provider)
    return { chatProviders: next, activeChatProvider: provider }
  }),
  closeChatProvider: (provider) => set((s) => {
    if (!s.chatProviders.includes(provider)) return {}
    const next = s.chatProviders.filter((p) => p !== provider)
    // Promote the previous tab as active when the active one closes —
    // mirrors the main TabBar's behavior on close.
    const nextActive: ChatProvider | null =
      s.activeChatProvider === provider
        ? (next[next.length - 1] ?? null)
        : s.activeChatProvider
    void window.api.setSetting('chatProviders', next)
    void window.api.setSetting('activeChatProvider', nextActive)
    return { chatProviders: next, activeChatProvider: nextActive }
  }),
  setActiveChatProvider: (provider) => set((s) => {
    if (!s.chatProviders.includes(provider)) return {}
    void window.api.setSetting('activeChatProvider', provider)
    return { activeChatProvider: provider }
  }),
  reorderChatProviders: (fromProvider, toProvider) => set((s) => {
    if (fromProvider === toProvider) return {}
    const from = s.chatProviders.indexOf(fromProvider)
    const to = s.chatProviders.indexOf(toProvider)
    if (from < 0 || to < 0) return {}
    const next = s.chatProviders.slice()
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    void window.api.setSetting('chatProviders', next)
    return { chatProviders: next }
  }),

  setOverlayState: (state) => set({ overlayState: state }),

  // ── Claude-session restart coordination ────────────────────────────
  markSessionRestarting: (sessionId) =>
    set((s) => {
      if (s.restartingSessionIds.has(sessionId)) return {}
      const next = new Set(s.restartingSessionIds)
      next.add(sessionId)
      return { restartingSessionIds: next }
    }),

  unmarkSessionRestarting: (sessionId) =>
    set((s) => {
      if (!s.restartingSessionIds.has(sessionId)) return {}
      const next = new Set(s.restartingSessionIds)
      next.delete(sessionId)
      return { restartingSessionIds: next }
    }),

  bumpSessionRestartToken: (sessionId) =>
    set((s) => ({
      sessionRestartTokens: {
        ...s.sessionRestartTokens,
        [sessionId]: (s.sessionRestartTokens[sessionId] || 0) + 1
      }
    })),

  // ── Deferred boot-restored tabs (see lib/deferredSpawn.ts) ─────────
  seedDeferredTabIds: (tabIds) =>
    set((s) => {
      // Once per run. React StrictMode double-invokes App's init effect, and a
      // second seed would re-defer — i.e. UNMOUNT — a tab the user had already
      // opened, tearing down a live terminal and orphaning its pty.
      if (s.deferredSeeded) return {}
      if (tabIds.length === 0) return { deferredSeeded: true }
      return { deferredSeeded: true, deferredTabIds: new Set(tabIds) }
    }),

  undeferTabs: (tabIds) =>
    set((s) => {
      const next = undeferOpenedTabs(s.deferredTabIds, tabIds)
      // Same instance back when nothing changed — this runs on every tab
      // switch, and a fresh Set each time would re-render the whole tab tree.
      return next === s.deferredTabIds ? {} : { deferredTabIds: next }
    }),

  restartAllClaudeSessions: async () => {
    const state = get()
    // Collect every sessionId that currently has a running PTY AND
    // belongs to a real Folder record (not a plain terminal,
    // which also lives in ptyMap under a different id namespace).
    const claudeSessionIds = new Set<string>()
    for (const list of Object.values(state.folders)) {
      for (const sess of list) {
        if (state.ptyMap[sess.id]) claudeSessionIds.add(sess.id)
      }
    }
    if (claudeSessionIds.size === 0) return

    // Mark each as "expected to die" BEFORE asking the main process to
    // kill the PTY — the pty-exit handler in ClaudeTerminal.tsx consults this
    // set and skips the tab-removal branch when it fires. Order matters:
    // marking after kill would allow the exit event to slip through
    // unflagged and the tab would vanish.
    const ids = Array.from(claudeSessionIds)
    ids.forEach((sid) => get().markSessionRestarting(sid))

    // Kill PTYs and clear mappings. Clearing upfront means the new
    // mount (triggered by the token bump below) won't reuse a dead
    // ptyId via `existingPtyId`.
    for (const sid of ids) {
      const pid = state.ptyMap[sid]
      if (!pid) continue
      try { await window.api.killClaudeSession(pid) } catch {/* ignore */}
      get().removePtyMapping(sid)
    }

    // Bump tokens to force a React remount. The Terminal component's
    // init guard (initializedRef) is per-instance, so a new key is the
    // cleanest way to rerun the spawn/resume flow (against the freshly
    // installed binary, or under the newly-selected account).
    ids.forEach((sid) => get().bumpSessionRestartToken(sid))

    // Defensive unmark — the pty-exit handler normally clears each
    // flag as the event arrives, but the handler may have been torn
    // down by unmount before the event landed. Left-behind marks
    // would cause the NEXT real exit to silently skip its tab-removal
    // cleanup, so we sweep them unconditionally. Idempotent with the
    // handler's own clear.
    setTimeout(() => {
      ids.forEach((sid) => get().unmarkSessionRestarting(sid))
    }, 250)
  },

  restartAllCodexSessions: async () => {
    // Codex account selection is global, so on switch/remove every RUNNING codex
    // session must restart to pick up the new CODEX_HOME. Mirrors
    // restartAllClaudeSessions but over codexSessions.
    const state = get()
    const ids: string[] = []
    for (const list of Object.values(state.codexSessions)) {
      for (const sess of list) { if (state.ptyMap[sess.id]) ids.push(sess.id) }
    }
    if (ids.length === 0) return
    ids.forEach((sid) => get().markSessionRestarting(sid))
    for (const sid of ids) {
      const pid = state.ptyMap[sid]
      if (!pid) continue
      try { await window.api.killClaudeSession(pid) } catch {/* ignore */}
      get().removePtyMapping(sid)
    }
    ids.forEach((sid) => get().bumpSessionRestartToken(sid))
    setTimeout(() => { ids.forEach((sid) => get().unmarkSessionRestarting(sid)) }, 250)
  },

  restartAllGrokSessions: async () => {
    // Grok account selection is global (like Codex), so on switch/remove every
    // RUNNING grok session must restart to pick up the new GROK_HOME.
    const state = get()
    const ids: string[] = []
    for (const list of Object.values(state.grokSessions)) {
      for (const sess of list) { if (state.ptyMap[sess.id]) ids.push(sess.id) }
    }
    if (ids.length === 0) return
    ids.forEach((sid) => get().markSessionRestarting(sid))
    for (const sid of ids) {
      const pid = state.ptyMap[sid]
      if (!pid) continue
      try { await window.api.killClaudeSession(pid) } catch {/* ignore */}
      get().removePtyMapping(sid)
    }
    ids.forEach((sid) => get().bumpSessionRestartToken(sid))
    setTimeout(() => { ids.forEach((sid) => get().unmarkSessionRestarting(sid)) }, 250)
  },

  restartAllGeminiSessions: async () => {
    // Gemini account selection is global (like Codex/Grok), so on switch/remove
    // every RUNNING gemini session must restart to pick up the new GEMINI_CLI_HOME.
    const state = get()
    const ids: string[] = []
    for (const list of Object.values(state.geminiSessions)) {
      for (const sess of list) { if (state.ptyMap[sess.id]) ids.push(sess.id) }
    }
    if (ids.length === 0) return
    ids.forEach((sid) => get().markSessionRestarting(sid))
    for (const sid of ids) {
      const pid = state.ptyMap[sid]
      if (!pid) continue
      try { await window.api.killClaudeSession(pid) } catch {/* ignore */}
      get().removePtyMapping(sid)
    }
    ids.forEach((sid) => get().bumpSessionRestartToken(sid))
    setTimeout(() => { ids.forEach((sid) => get().unmarkSessionRestarting(sid)) }, 250)
  },

  restartAllKimiSessions: async () => {
    // Kimi account selection is global (like Codex/Grok/Gemini), so on
    // switch/remove every RUNNING kimi session must restart to pick up the new
    // KIMI_SHARE_DIR.
    const state = get()
    const ids: string[] = []
    for (const list of Object.values(state.kimiSessions)) {
      for (const sess of list) { if (state.ptyMap[sess.id]) ids.push(sess.id) }
    }
    if (ids.length === 0) return
    ids.forEach((sid) => get().markSessionRestarting(sid))
    for (const sid of ids) {
      const pid = state.ptyMap[sid]
      if (!pid) continue
      try { await window.api.killClaudeSession(pid) } catch {/* ignore */}
      get().removePtyMapping(sid)
    }
    ids.forEach((sid) => get().bumpSessionRestartToken(sid))
    setTimeout(() => { ids.forEach((sid) => get().unmarkSessionRestarting(sid)) }, 250)
  },

  restartClaudeSession: async (sessionId) => {
    const pid = get().ptyMap[sessionId]
    get().markSessionRestarting(sessionId)
    if (pid) {
      try { await window.api.killClaudeSession(pid) } catch {/* ignore */}
      get().removePtyMapping(sessionId)
    }
    get().bumpSessionRestartToken(sessionId)
    setTimeout(() => get().unmarkSessionRestarting(sessionId), 250)
  },

  switchAllSessionsToAccount: async (accountId) => {
    const state = get()
    const value = accountId === 'default' ? undefined : accountId
    // Persist the account on every Claude folder (so future + resumed sessions use it).
    for (const [projectId, list] of Object.entries(state.folders)) {
      for (const folder of list) {
        if (folder.isWorkspace) continue
        get().updateFolder(projectId, folder.id, { accountId: value })
        try { await window.api.saveFolder({ ...folder, accountId: value }) } catch {/* ignore */}
      }
    }
    // Restart the ones currently running so they pick up the new account now.
    await get().restartAllClaudeSessions()
  },

  reassignSessionsToDefault: async (removedAccountId) => {
    // An account was deleted (its config dir is gone). Move every folder that ran
    // as it back to the default account, and restart the running ones so they
    // reconnect under ~/.claude (transcripts are in the shared store, so the
    // conversation resumes). Without this they'd point at a deleted config dir.
    const state = get()
    const affected: string[] = []
    for (const [projectId, list] of Object.entries(state.folders)) {
      for (const folder of list) {
        if (folder.accountId !== removedAccountId) continue
        affected.push(folder.id)
        get().updateFolder(projectId, folder.id, { accountId: undefined })
        try { await window.api.saveFolder({ ...folder, accountId: undefined }) } catch {/* ignore */}
      }
    }
    for (const sid of affected) {
      if (get().ptyMap[sid]) await get().restartClaudeSession(sid)
    }
  },
}))

// Keep agentCapabilities fresh without any component having to mount a
// listener: main probes CLI capabilities in the background (stale-while-
// revalidate — see electron/main/agent-capabilities.ts) and pushes this event
// only when the result actually changed. Subscribed once at module load.
if (typeof window !== 'undefined' && window.api?.onAgentCapabilitiesUpdated) {
  window.api.onAgentCapabilitiesUpdated((capabilities) => {
    useAppStore.setState({ agentCapabilities: capabilities })
  })
}
